import {
  isTemplateCreateOperation,
  type TemplateCreateOperation,
} from '@festina/configuration-general-types/configuration'
import { LayerKey } from '@festina-life/api'
import { assertNotEmpty } from '@festina-life/core'
import { useEffect, useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'
import { isWorkspaceTemplateCreateOperation } from '../templateOperations/types'
import type { OperationWithSourceInfo } from '../types'
import { useWorkspaceOperations } from '../workspace/useWorkspaceOperations'
import { addKeyValuePairToMap, toReferenceModel } from './helpers'

export const useTemplateReferenceModel = (name?: string | null) => {
  const { workspaceKey } = useOperationsContext()
  const { requestLayerMetaModel, getLayerMetaModel, blockTopology } =
    useMetaModelContext()
  const { data: draftOperations } = useWorkspaceOperations(
    workspaceKey,
    isWorkspaceTemplateCreateOperation,
  )
  const { applicationTemplates: applicationTemplateOperations } =
    useApplicationViewOperationsContext()
  const rootBlock = blockTopology.name

  const operationsByClass = useMemo(() => {
    const classMap = new Map<
      string,
      Map<string, OperationWithSourceInfo<TemplateCreateOperation>[]>
    >()
    applicationTemplateOperations?.forEach(
      ({ lastEntity, operations }, key) => {
        const createOperations = operations.filter((operation) =>
          isTemplateCreateOperation(operation.internalOperation),
        )

        if (createOperations.length > 0 && lastEntity) {
          const className = lastEntity.templateTypeName

          assertNotEmpty(className)
          addKeyValuePairToMap({
            classMap,
            className,
            key,
            value: createOperations,
          })
        }
      },
    )

    draftOperations.forEach((value) => {
      assertNotEmpty(value.operation?.entity)

      const id = value.operation.entity.name
      const className = value.operation.entity.templateTypeName

      if (!id || !className) {
        return
      }
      addKeyValuePairToMap({
        classMap,
        className,
        key: id,
        value: [
          {
            isDraft: true,
            layer: new LayerKey({
              layerId: value.layer ?? null,
              tenantId: value.tenantId ?? null,
            }),
            block: value.block ?? rootBlock,
            actions: [],
            changesetHistory: null,
            internalOperation: value.operation,
            annulOperations: [],
          },
        ],
      })
    })

    return classMap
  }, [applicationTemplateOperations, draftOperations, rootBlock])

  const involvedLayerIds = useMemo(
    () =>
      Array.from(
        new Set(
          Array.from(operationsByClass.values()).flatMap((value) =>
            Array.from(value.values()).flatMap((operations) =>
              operations.map((operation) => operation.layer?.layerId ?? null),
            ),
          ),
        ),
      ),
    [operationsByClass],
  )

  useEffect(() => {
    involvedLayerIds.forEach((layerId) => requestLayerMetaModel(layerId))
  }, [involvedLayerIds, requestLayerMetaModel])

  return useMemo(() => {
    const layeredMetaModel = new Map(
      involvedLayerIds.map((layerId) => [layerId, getLayerMetaModel(layerId)]),
    )
    if (name && layeredMetaModel) {
      return toReferenceModel({
        operations: operationsByClass,
        name,
        layeredMetaModel,
      })
    }

    return null
  }, [name, operationsByClass, getLayerMetaModel, involvedLayerIds])
}
